import { Anchor, Box, Text } from "@mantine/core"
import { ALL_VALUE } from "./constants.util"
import { createKeyboardHandler } from "./keyboard.utils"

export interface StylizeTextOptions {
	fwa?: number | object
	fw?: number | object
	fz?: number | object
	color?: string
	lh?: number | object
	onAnchorClick?: () => void
	dataQaAttribute?: string
	variables?: (string | number | undefined)[]
	linkFw?: number | object // Font weight specifically for links
}

export function stylizeText(str: string, options?: StylizeTextOptions) {
	if (!str) return null

	const regex = /(\*[^*]+\*|\[[^\]]+\]|\[[^\]]+\]|~[^~]+~|\{\})/g
	const parts = str.split(regex)
	let variableIndex = 0

	return (
		<Box component="span">
			{parts.map((part: string, index: number) => {
				const uniqueKey = `styled-text-${index}-${part}`

				if (part.startsWith("*") && part.endsWith("*")) {
					return (
						<Text
							key={uniqueKey}
							lh={options?.lh}
							fz={options?.fz}
							fw={options?.fw ?? 700}
							span
						>
							{part.replace(/\*/g, "")}
						</Text>
					)
				}

				if (part.startsWith("~") && part.endsWith("~")) {
					return (
						<Text
							key={uniqueKey}
							lh={options?.lh}
							fz={options?.fz}
							fw={options?.fw}
							fs="italic"
							span
						>
							{part.replace(/~/g, "")}
						</Text>
					)
				}

				if (part === "{}") {
					const variableValue = options?.variables?.[variableIndex]
					variableIndex++

					return (
						<Text
							key={uniqueKey}
							lh={options?.lh}
							fz={options?.fz}
							c={options?.color}
							fw={options?.fw}
							span
						>
							{variableValue !== undefined ? String(variableValue) : "{}"}
						</Text>
					)
				}

				if (part.startsWith("[") && part.includes("]")) {
					const linkMatch = part.match(/\[([^\]]+)\]/)
					if (linkMatch) {
						const linkText = linkMatch[1]
						const handleKeyDown = createKeyboardHandler(
							() => options?.onAnchorClick?.(),
							{ debounceMs: 100 }
						)

						return (
							<Anchor
								key={uniqueKey}
								component="button"
								lh={options?.lh}
								fz={options?.fz ?? 14}
								fw={options?.fwa || options?.linkFw || options?.fw}
								underline="always"
								c={options?.color ?? "unset"}
								onClick={() => options?.onAnchorClick?.()}
								onKeyDown={handleKeyDown}
								tabIndex={0}
								data-qa={options?.dataQaAttribute}
								style={{
									background: "none",
									border: "none",
									padding: 0,
									cursor: "pointer"
								}}
							>
								{linkText}
							</Anchor>
						)
					}
				}

				return (
					<Text fz={options?.fz} fw={options?.fw} key={uniqueKey} span>
						{part}
					</Text>
				)
			})}
		</Box>
	)
}

export const capitalizeWords = (value: string): string => {
	const exceptions = new Map([
		["ny", "NY"],
		["nj", "NJ"],
		["fl", "FL"],
		["usa", "USA"],
		["qld", "QLD"]
	])

	const capitalizePart = (part: string): string => {
		if (/^([a-z]\.)+$/i.test(part)) {
			return part
				.split(".")
				.map((subPart) => subPart.toUpperCase())
				.join(".")
		}

		const lowerPart = part?.toLowerCase()
		if (exceptions.has(lowerPart)) {
			return exceptions.get(lowerPart) || part
		}
		return part[0]?.toUpperCase() + part.slice(1)?.toLowerCase()
	}

	return value
		.split(/(\s|\/|[(),-])/g)
		.map((part) => {
			const trimmed = part.trim()
			return /[a-zA-Z]/.test(trimmed) ? capitalizePart(trimmed) : part
		})
		.join("")
}

const pushToTop = <T extends { label: string; value: string }>(
	array: T[],
	topItems: string[]
): T[] => {
	const priorityOptions = array.filter((port) => topItems.includes(port.value))
	const otherOptions = array.filter((port) => !topItems.includes(port.value))
	return [...priorityOptions, ...otherOptions]
}

export const alphabeticalSort = <T extends { label: string; value: string }>(
	array: T[]
) => {
	const allElement = array.find((item) => item.value === ALL_VALUE)
	const pushToTopItems = ["PCC", "PDM"]
	const hasPriorityItems = array.some((item) =>
		pushToTopItems.includes(item.value)
	)
	const filteredArray = array
		.filter((item) => item.value !== ALL_VALUE)
		.map((item) => ({
			...item,
			label: capitalizeWords(item.label)
		}))

	let sortedArray = filteredArray.sort((a, b) => a.label.localeCompare(b.label))

	if (hasPriorityItems) {
		sortedArray = pushToTop(sortedArray, pushToTopItems)
	}

	const finalArray = allElement ? [allElement, ...sortedArray] : sortedArray
	return finalArray
}
